"use client";

import React, { Suspense, useState, useEffect, useLayoutEffect } from "react";
import { notFound } from "next/navigation";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useSession } from "next-auth/react";
import ButtonAccount from "@/components/ButtonAccount";
import CreditsDisplay from "@/components/CreditsDisplay";
import DashboardHeader from "@/components/headers/DashboardHeader";
import LoadingSpinner from "@/components/LoadingSpinner";
import ToolsSidebarV2 from "@/app/editor-v2/_components/ToolsSidebarV2";
import SharedImageScene from "../_components/SharedImageScene";
import { toolsRegistry, ToolInfo } from "@/contexts/ToolsContext";
import { useCreditsContext } from "@/contexts/CreditsContext";
import GlobalEditorProvider from "../_providers/GlobalEditorProvider";
import { trackFunnel } from "@/libs/analytics";
import EditorV2Shell from "@/app/editor-v2/_components/EditorV2Shell";

/** Tools that keep their /editor/* URL but render in the v2 layout. */
const V2_SHELL_TOOL_IDS = new Set(["ai-designer"]);

// Reusable loading component for dynamic imports
const ToolLoadingFallback = () => (
  <div className="fixed inset-0 flex flex-col items-center justify-center bg-gray-50 z-50">
    <LoadingSpinner size="lg" text="Loading tool..." fullScreen={false} />
  </div>
);

// Map of toolId to config component
const toolConfigComponents: Record<string, any> = {
  "ai-headshots": dynamic(() => import("../tools/ai-headshots"), {
    loading: ToolLoadingFallback,
  }),
  "ai-headshots-dev": dynamic(() => import("../tools/ai-headshots-dev"), {
    loading: ToolLoadingFallback,
  }),
  "ai-avatars": dynamic(() => import("../tools/ai-avatars"), {
    loading: ToolLoadingFallback,
  }),
  "ai-avatars-dev": dynamic(() => import("../tools/ai-avatars-dev"), {
    loading: ToolLoadingFallback,
  }),
  "change-clothes": dynamic(() => import("../tools/change-clothes"), {
    loading: ToolLoadingFallback,
  }),
  "change-hair": dynamic(() => import("../tools/change-hair"), {
    loading: ToolLoadingFallback,
  }),
  "ai-makeup": dynamic(() => import("../tools/ai-makeup"), {
    loading: ToolLoadingFallback,
  }),
  "change-scene": dynamic(() => import("../tools/change-scene"), {
    loading: ToolLoadingFallback,
  }),
  "add-accessories": dynamic(() => import("../tools/add-accessories"), {
    loading: ToolLoadingFallback,
  }),
  "remove-accessories": dynamic(() => import("../tools/remove-accessories"), {
    loading: ToolLoadingFallback,
  }),
  "clear-skin": dynamic(() => import("../tools/clear-skin"), {
    loading: ToolLoadingFallback,
  }),
  "add-smile": dynamic(() => import("../tools/add-smile"), {
    loading: ToolLoadingFallback,
  }),
  "ai-branding": dynamic(() => import("../tools/ai-branding"), {
    ssr: false,
    loading: ToolLoadingFallback,
  }),
  "add-logo-watermark": dynamic(() => import("../tools/add-logo-watermark"), {
    ssr: false,
    loading: ToolLoadingFallback,
  }),
  "ai-background": dynamic(() => import("../tools/ai-background"), {
    loading: ToolLoadingFallback,
  }),
  "age-filter": dynamic(() => import("../tools/age-filter"), {
    loading: ToolLoadingFallback,
  }),
  "gender-swap": dynamic(() => import("../tools/gender-swap"), {
    loading: ToolLoadingFallback,
  }),
  "cartoonizer": dynamic(() => import("../tools/cartoonizer"), {
    loading: ToolLoadingFallback,
  }),
  "anime-styling": dynamic(() => import("../tools/anime-styling"), {
    loading: ToolLoadingFallback,
  }),
  "ai-designer": dynamic(() => import("../tools/ai-designer"), {
    loading: ToolLoadingFallback,
  }),
  "ai-designer-dev": dynamic(() => import("../tools/ai-designer-dev"), {
    loading: ToolLoadingFallback,
  }),
  "hd-detailer": dynamic(() => import("../tools/hd-detailer"), {
    loading: ToolLoadingFallback,
  }),
  "photo-filters": dynamic(() => import("../tools/photo-filters"), {
    loading: ToolLoadingFallback,
  }),
  "basic-headshots": dynamic(() => import("../tools/basic-headshots"), {
    loading: ToolLoadingFallback,
  }),
  "change-light-dev": dynamic(() => import("../tools/change-light-dev"), {
    loading: ToolLoadingFallback,
  }),
  // Add more tools here as we migrate them
};

interface EditorPageProps {
  params: {
    toolId: string;
  };
}

// Component that just renders the tool UI (editor context now provided globally)
function EditorWithHistory({ toolInfo }: { toolInfo: ToolInfo }) {
  const [sidebarWidth, setSidebarWidth] = useState('w-full md:w-[420px]');
  const { credits, isLoading: creditsLoading } = useCreditsContext();

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [creditsLoading]);

  // Track tool editor load
  useEffect(() => {
    if (!creditsLoading && credits !== null) {
      trackFunnel('tool_engagement', 'tool_loaded', {
        tool_id: toolInfo.id,
        tool_name: toolInfo.title,
        credits_required: toolInfo.creditCost, // toolInfo is validated to exist with valid creditCost
        user_credits: credits
      });
    }
  }, [toolInfo.id, toolInfo.title, toolInfo.creditCost, credits, creditsLoading]);

  // Show loading spinner while credits are being fetched
  if (creditsLoading) {
    return <LoadingSpinner fullScreen text="Loading editor..." />;
  }

  // Get the config component for this tool
  const ConfigComponent = toolConfigComponents[toolInfo.id];

  return (
    <GlobalEditorProvider toolInfo={toolInfo}>
      <div className="flex min-h-[calc(100dvh-var(--promo-banner-height,0px))] flex-col bg-gray-50 md:h-[calc(100dvh-var(--promo-banner-height,0px))] md:max-h-[calc(100dvh-var(--promo-banner-height,0px))] md:overflow-hidden">
        {/* Header */}
        <DashboardHeader variant="editor" title={toolInfo.title} />

        <div className="flex flex-col md:flex-row flex-1 relative md:overflow-hidden">
          {toolInfo.id === 'basic-headshots' ? (
            <>
              <ToolsSidebarV2 currentToolId={toolInfo.id} />
              <Suspense fallback={<div>Loading...</div>}>
                <ConfigComponent />
              </Suspense>
            </>
          ) : (
            <>
              {/* Image Viewer/Scene - Shows first on mobile, last on desktop */}
              <div className="flex-1 order-1 md:order-3 flex flex-col min-h-[50vh] md:min-h-0">
                <SharedImageScene toolInfo={toolInfo} />
              </div>

              {/* Tools Navigation - Between image and config on mobile, leftmost column on desktop */}
              <ToolsSidebarV2 currentToolId={toolInfo.id} className="order-2 md:order-1" />

              {/* Tool Config Panel */}
              <div data-config-panel className={`${sidebarWidth} bg-white border-t md:border-t-0 md:border-r border-gray-200 flex flex-col transition-all duration-300 order-3 md:order-2 min-h-[50vh] md:min-h-0 relative`}>
                <Suspense fallback={<div>Loading...</div>}>
                  <ConfigComponent />
                </Suspense>
              </div>
            </>
          )}
        </div>
      </div>
    </GlobalEditorProvider>
  );
}

export default function EditorPage({ params }: EditorPageProps) {
  const { toolId } = params;
  const { data: session } = useSession();
  const { credits, refreshCredits } = useCreditsContext();

  // Check if tool exists in registry
  const toolInfo = toolsRegistry.find(t => t.id === toolId);
  if (!toolInfo) {
    notFound();
  }

  // Get the config component for this tool
  const ConfigComponent = toolConfigComponents[toolId];
  if (!ConfigComponent) {
    // Tool exists but not yet migrated
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Tool Not Yet Available</h2>
          <p className="text-gray-600">This tool is being updated. Please check back soon.</p>
        </div>
      </div>
    );
  }

  if (V2_SHELL_TOOL_IDS.has(toolId)) {
    return <EditorV2Shell toolInfo={toolInfo} />;
  }

  return <EditorWithHistory toolInfo={toolInfo} />;
}
